import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, Shield, DollarSign, CreditCard, PiggyBank } from 'lucide-react';
import CustomSelect from '../shared/CustomSelect';

export const ProductFormModal = ({
                                     isOpen,
                                     onClose,
                                     onSubmit,
                                     companies = [],
                                     product = null,
                                     loading = false
                                 }) => {
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        description: '',
        type: 'INSURANCE',
        minAmount: 0,
        maxAmount: 0,
        companyId: '',
        active: true
    });

    const [errors, setErrors] = useState({});

    const productTypes = [
        { value: 'INSURANCE', label: 'Insurance', icon: Shield, color: 'blue' },
        { value: 'INVESTMENT', label: 'Investment', icon: DollarSign, color: 'green' },
        { value: 'LOAN', label: 'Loan', icon: CreditCard, color: 'purple' },
        { value: 'SAVINGS', label: 'Savings', icon: PiggyBank, color: 'yellow' }
    ];

    useEffect(() => {
        if (product) {
            setFormData({
                code: product.code || '',
                name: product.name || '',
                description: product.description || '',
                type: product.type || 'INSURANCE',
                minAmount: product.minAmount || 0,
                maxAmount: product.maxAmount || 0,
                companyId: product.companyId || '',
                active: product.active !== undefined ? product.active : true
            });
        } else {
            // Reset form for new product
            setFormData({
                code: '',
                name: '',
                description: '',
                type: 'INSURANCE',
                minAmount: 0,
                maxAmount: 0,
                companyId: companies[0]?.id || '',
                active: true
            });
        }
        setErrors({});
    }, [product, companies, isOpen]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.code.trim()) newErrors.code = 'Product code is required';
        if (!formData.name.trim()) newErrors.name = 'Product name is required';
        if (!formData.companyId) newErrors.companyId = 'Please select a company';
        if (formData.minAmount < 0) newErrors.minAmount = 'Minimum amount cannot be negative';
        if (formData.maxAmount < formData.minAmount) {
            newErrors.maxAmount = 'Maximum amount must be greater than minimum amount';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            onSubmit(formData);
        }
    };

    if (!isOpen) return null;

    const selectedType = productTypes.find(t => t.value === formData.type);

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                    onClick={onClose}
                />

                <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xl font-semibold text-gray-900">
                                {product ? 'Edit Product' : 'Add New Product'}
                            </h3>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            {/* Product Code */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Product Code *
                                </label>
                                <input
                                    type="text"
                                    value={formData.code}
                                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        errors.code ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="e.g., PROD001"
                                />
                                {errors.code && (
                                    <p className="mt-1 text-sm text-red-600">{errors.code}</p>
                                )}
                            </div>

                            {/* Product Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Product Name *
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        errors.name ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="Enter product name"
                                />
                                {errors.name && (
                                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                                )}
                            </div>

                            {/* Company */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Company *
                                </label>
                                <CustomSelect
                                    value={formData.companyId}
                                    onChange={(val) => setFormData({...formData, companyId: val})}
                                    options={companies.map(company => ({
                                        value: company.id,
                                        label: `${company.name} (${company.code})`
                                    }))}
                                    placeholder="Select a company"
                                    disabled={product !== null}
                                    error={!!errors.companyId}
                                    searchable
                                />
                                {errors.companyId && (
                                    <p className="mt-1 text-sm text-red-600">{errors.companyId}</p>
                                )}
                            </div>

                            {/* Product Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Product Type
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    {productTypes.map(type => {
                                        const Icon = type.icon;
                                        const isSelected = formData.type === type.value;
                                        return (
                                            <button
                                                key={type.value}
                                                type="button"
                                                onClick={() => setFormData({...formData, type: type.value})}
                                                className={`flex items-center gap-2 px-3 py-2 border rounded-lg transition ${
                                                    isSelected
                                                        ? `border-${type.color}-500 bg-${type.color}-50 text-${type.color}-700`
                                                        : 'border-gray-300 hover:bg-gray-50'
                                                }`}
                                            >
                                                <Icon className="w-4 h-4" />
                                                <span className="text-sm">{type.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Minimum Amount */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Minimum Amount
                                </label>
                                <input
                                    type="number"
                                    value={formData.minAmount}
                                    onChange={(e) => setFormData({...formData, minAmount: parseFloat(e.target.value) || 0})}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        errors.minAmount ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    min="0"
                                    step="0.01"
                                />
                                {errors.minAmount && (
                                    <p className="mt-1 text-sm text-red-600">{errors.minAmount}</p>
                                )}
                            </div>

                            {/* Maximum Amount */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Maximum Amount
                                </label>
                                <input
                                    type="number"
                                    value={formData.maxAmount}
                                    onChange={(e) => setFormData({...formData, maxAmount: parseFloat(e.target.value) || 0})}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        errors.maxAmount ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    min="0"
                                    step="0.01"
                                />
                                {errors.maxAmount && (
                                    <p className="mt-1 text-sm text-red-600">{errors.maxAmount}</p>
                                )}
                            </div>

                            {/* Description */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    rows="3"
                                    placeholder="Enter product description..."
                                />
                            </div>

                            {/* Status */}
                            <div className="md:col-span-2">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={formData.active}
                                        onChange={(e) => setFormData({...formData, active: e.target.checked})}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm font-medium text-gray-700">Active Product</span>
                                </label>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        {product ? 'Update' : 'Create'} Product
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ProductFormModal;
